import { getGameQuizReuploadImpact } from "../../../services/gameQuiz/gameQuiz.service";
import type { GameQuizReuploadImpact } from "../../../services/gameQuiz/gameQuiz.types";
import { getUploadCheck } from "./contentHub.service";
import type { HubRow } from "./hubTypes";
import { deriveRowState, isReadyRow } from "./rowState";
import type { ReadyRow, StateOptions } from "./rowState";

/**
 * SCRUM-1581: a quiz an upload run would replace that homework or student
 * attempts outside Emmi's own schools already reach. The page asks once,
 * before the run, whether to replace all of them.
 */
export interface QuizWithRecords {
  rowNumber: number;
  clave: string;
  courseTitle: string;
  gameQuizId: number;
  impact: GameQuizReuploadImpact;
}

const LOOKUPS_IN_FLIGHT = 4;

// The quiz a row's YAML replaces is the course's first one, as the platform
// picks it: a course holds quizzes of one kind only (a standalone and a video
// quiz at once fail on their own), so the first is also the first video quiz
// a video YAML replaces. Only a video YAML over standalone quizzes is refused
// instead; listing that row costs a line of the warning, and the runner still
// overwrites nothing but the quiz it replaces.
async function lookUp(row: ReadyRow): Promise<QuizWithRecords | null> {
  const check = await getUploadCheck(row.courseId);
  if (check.doubleQuizError || check.quizzes.length === 0) return null;
  const { gameQuizId } = check.quizzes[0];
  const impact = await getGameQuizReuploadImpact(gameQuizId);
  if (!impact.requires_confirmation) return null;
  return {
    rowNumber: row.rowNumber,
    clave: row.clave,
    courseTitle: row.courseTitle,
    gameQuizId,
    impact,
  };
}

// Rejects when a course or a quiz cannot be read: the run must not start as
// if nothing were reached.
export async function findQuizzesWithRecords(
  rows: HubRow[],
  options: StateOptions,
): Promise<QuizWithRecords[]> {
  const queue = rows.filter(
    (row): row is ReadyRow =>
      isReadyRow(row) && deriveRowState(row, options).quiz.status === "pending",
  );
  const found: QuizWithRecords[] = [];
  const worker = async () => {
    for (let row = queue.shift(); row; row = queue.shift()) {
      const quiz = await lookUp(row);
      if (quiz) found.push(quiz);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(LOOKUPS_IN_FLIGHT, queue.length) }, worker),
  );
  return found.sort((a, b) => a.rowNumber - b.rowNumber);
}
